/**
 * cursosStore.js — store en memoria (sin backend) de la sección «Cursos» del simulador de autoescuela.
 * Espeja la API de apps/driving-school/src/api/cursosApi.js (getCursos, getEdicion, saveCurso,
 * saveEdicion, addInscripcion, accionInscripcion, emitirCertificados) con las reglas del backend
 * (aws_lambdas/_shared_cursos): plazas por contador, lista de espera numerada, oferta de plaza al
 * liberarse una, certificados solo con todas las sesiones y CAP renovado (5 años) en la ficha.
 * Los errores salen con la forma de axios ({ response: { data: { error } } }) para que
 * `mensajeError` funcione igual que en la real. Datos relativos a hoy: la demo nunca caduca.
 * Diseño: docs/features/cursos-autoescuela.md.
 */
import dayjs from "dayjs";
import { SECTOR_DATA_MAP } from "../../mockData";
import { asistenciaCompleta, esCap, horasTotales } from "./cursos/cursosUtils";

// Número compartido de WhatsApp de las autoescuelas (el del enlace wa.me de la real).
export const CURSOS_WA_NUMBER = "+14434322746";

// Latencia simulada de las acciones (para que se vea el «cargando» como en la real).
export const DEMO_LATENCY_MS = 250;
// Tras liberarse una plaza, el primero de la lista pulsa «Lo quiero» a los N ms (solo demo).
export const OFERTA_ACEPTADA_MS = 4000;

const fail = (code) => {
  const err = new Error(code);
  err.response = { status: 400, data: { error: code } };
  return err;
};
const wait = (ms = DEMO_LATENCY_MS) => new Promise((r) => setTimeout(r, ms));

const ymd = (d) => d.format("YYYY-MM-DD");
// Días laborables (L-V) desde `start`, `n` sesiones del mismo horario.
function sesionesLaborables(prefix, start, n, inicio, fin) {
  const out = [];
  let d = start;
  while (out.length < n) {
    const dow = d.day();
    if (dow !== 0 && dow !== 6) out.push({ sesion_id: `${prefix}-s${out.length + 1}`, fecha: ymd(d), inicio, fin });
    d = d.add(1, "day");
  }
  return out;
}
const nextMonday = (weeks = 0) => {
  const t = dayjs().startOf("day");
  const add = ((8 - t.day()) % 7) || 7;
  return t.add(add + weeks * 7, "day");
};

const LETRAS = "TRWAGMYFPDXBNJZSQVHLCKE";
const dniDe = (n) => `${String(n).padStart(8, "0")}${LETRAS[n % 23]}`;

function alumnoPorId(id) {
  return (SECTOR_DATA_MAP.autoescuela?.mockAlumnos || []).find((a) => a.alumno_id === id) || null;
}
function profesor(id) {
  const p = (SECTOR_DATA_MAP.autoescuela?.mockInstructors || []).find((i) => i.instructor_id === id);
  return p ? { profesor_id: p.instructor_id, profesor_nombre: p.name } : { profesor_id: null, profesor_nombre: null };
}

// Personas de los cursos (conductores profesionales y gente que recupera puntos).
const PERSONAS = [
  ["José Antonio", "Gil Moreno"], ["Francisco", "Navarro Peña"], ["Manuel", "Ortega Ruiz"], ["Rafael", "Serrano Vidal"],
  ["Antonio", "Castillo Mora"], ["David", "Molina Rubio"], ["Juan Carlos", "Delgado Prieto"], ["Miguel", "Herrera Sanz"],
  ["Rocío", "Vega Marín"], ["Fernando", "Cano Iglesias"], ["Luis", "Ramos Garrido"], ["Pilar", "Méndez Cortés"],
  ["Jesús", "Lozano Gallego"], ["Ángel", "Cruz Santos"], ["Raúl", "Medina Fuentes"], ["Óscar", "Guerrero León"],
  ["Ramón", "Blanco Pascual"], ["Enrique", "Calvo Nieto"], ["Tomás", "Arias Bravo"], ["Vicente", "Rojas Soler"],
  ["Cristina", "Vargas Pardo"], ["Joaquín", "Sáez Carmona"], ["Emilio", "Benítez Lorenzo"], ["Inmaculada", "Peña Hidalgo"],
  ["Gonzalo", "Campos Vicente"], ["Agustín", "Reyes Nadal"], ["Beatriz", "Ibáñez Durán"], ["Salvador", "Crespo Rivas"],
  ["Andrés", "Montero Aguilar"], ["Mercedes", "Santana Gil"], ["Ignacio", "Pastor Esteban"], ["Nuria", "Fuentes Mora"],
  ["Ricardo", "Soto Varela"], ["Marina", "Garrido Luna"], ["Alberto", "Rubio Ferrer"], ["Patricia", "León Cabrera"],
  ["Felipe", "Marín Osuna"], ["Julián", "Pacheco Ruano"], ["Eduardo", "Quintana Roldán"], ["Sergio", "Velasco Amor"],
  ["Lorenzo", "Bermúdez Sáez"], ["Arturo", "Carrasco Peral"], ["Germán", "Toledo Vera"], ["Rosa", "Domínguez Lara"],
  ["Mariano", "Esteve Llorens"], ["Pedro", "Galán Montes"], ["Héctor", "Barrios Polo"], ["Amparo", "Ferrándiz Ros"],
];

let _seq = 0;
const nid = (p) => `${p}-${Date.now().toString(36)}-${(_seq += 1).toString(36)}`;

function persona(idx, extra = {}) {
  const [nombre, apellidos] = PERSONAS[idx % PERSONAS.length];
  const slug = nombre.split(" ")[0].toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
  return {
    nombre,
    apellidos,
    phone: `+3462${String(1000000 + idx * 7919).slice(-7)}`,
    dni: dniDe(20145000 + idx * 1373),
    email: idx % 3 === 0 ? `${slug}.${apellidos.split(" ")[0].toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")}@gmail.com` : "",
    ...extra,
  };
}
function desdeAlumno(alumnoId, extra = {}) {
  const a = alumnoPorId(alumnoId);
  if (!a) return null;
  const [nombre, ...rest] = String(a.name).split(" ");
  return {
    nombre,
    apellidos: rest.join(" "),
    phone: a.phone,
    dni: a.dni || "",
    email: "",
    alumno_id: a.alumno_id,
    contact_id: `contact-${a.alumno_id}`,
    ...(a.cap?.caducidad ? { cap_caducidad_actual: a.cap.caducidad } : {}),
    ...extra,
  };
}

function insc(edicion, datos, extra = {}) {
  return {
    inscripcion_id: nid("insc"),
    edicion_id: edicion.edicion_id,
    asistencia: {},
    pago: "no_aplica",
    origen: "whatsapp",
    created_at: dayjs().subtract(3, "day").toISOString(),
    ...datos,
    ...extra,
  };
}

let state = null;
let _selectedEdicionId = null;
const _subs = new Set();
const _eventSubs = new Set();
const _timers = new Set();

function emit() {
  _subs.forEach((fn) => { try { fn(); } catch { /* un suscriptor roto no tumba a los demás */ } });
}
function emitEvent(ev) {
  _eventSubs.forEach((fn) => { try { fn(ev); } catch { /* idem */ } });
}

function seed() {
  _seq = 0;
  const hoy = dayjs().startOf("day");
  const cursos = [
    {
      curso_id: "cur-cap-continuo", tipo: "cap_continuo", nombre: "CAP formación continua 35 h", horas: 35, precio_cents: 25000, activo: true,
      descripcion: "Renovación obligatoria del CAP cada 5 años para conductores profesionales de camión y autobús.",
    },
    {
      curso_id: "cur-puntos", tipo: "recuperacion_puntos", nombre: "Recuperación de puntos", horas: 12, precio_cents: 19500, activo: true,
      descripcion: "Curso de sensibilización y reeducación vial para recuperar hasta 6 puntos del permiso.",
    },
    {
      curso_id: "cur-cap-inicial", tipo: "cap_inicial", nombre: "CAP inicial acelerado", horas: 140, precio_cents: 115000, activo: true,
      descripcion: "Cualificación inicial para conducir camión o autobús de forma profesional (140 h).",
    },
  ];

  const ediciones = [];
  const inscripciones = [];

  // ── CAP formación continua: 5 días de 7 h, 18/20 con 2 en espera (se liberaron 2 plazas y ya se les han ofrecido) ──
  const capIni = nextMonday(1);
  const eCap = {
    edicion_id: "ed-cap-continuo-1", curso_id: "cur-cap-continuo", curso_nombre: "CAP formación continua 35 h", curso_tipo: "cap_continuo",
    codigo: "CUR-7KQ2MX", estado: "abierta", lugar: "Aula 1 · sede centro", plazas_max: 20, plazas_min: 8,
    ...profesor("inst-002"), sesiones: sesionesLaborables("ed-cap-continuo-1", capIni, 5, "08:00", "15:00"),
  };
  ediciones.push(eCap);
  const capPlaza = [desdeAlumno("alu-001", { permiso: "C", origen: "crm" }), desdeAlumno("alu-006", { permiso: "CD" })].filter(Boolean);
  for (let i = 0; capPlaza.length < 18; i += 1) {
    capPlaza.push(persona(i, {
      permiso: i % 4 === 0 ? "D" : "C",
      cap_caducidad_actual: ymd(hoy.add(20 + i * 9, "day")),
    }));
  }
  capPlaza.forEach((p, i) => {
    inscripciones.push(insc(eCap, p, {
      estado: "plaza",
      pago: i % 5 === 3 ? "pendiente" : "pagado",
      asistencia_confirmada: false,
      ...(i === 7 ? { datos_pendientes: true, dni: "", email: "" } : {}),
      ...(i === 11 ? { dni_revisar: true } : {}),
    }));
  });
  inscripciones.push(insc(eCap, persona(18, { permiso: "C" }), { estado: "espera", puesto_espera: 1, oferta_at: dayjs().subtract(40, "minute").toISOString() }));
  inscripciones.push(insc(eCap, persona(19, { permiso: "C" }), { estado: "espera", puesto_espera: 2, oferta_at: dayjs().subtract(40, "minute").toISOString() }));
  inscripciones.push(insc(eCap, persona(20, { permiso: "D" }), { estado: "cancelada", cancel_origen: "whatsapp", pago: "pagado" }));
  inscripciones.push(insc(eCap, persona(21, { permiso: "C" }), { estado: "cancelada", cancel_origen: "crm", origen: "crm" }));

  // ── Recuperación de puntos: 3 tardes de 4 h, 9/15 (mínimo 10) ──
  const ptsIni = hoy.add(4, "day");
  const ePts = {
    edicion_id: "ed-puntos-1", curso_id: "cur-puntos", curso_nombre: "Recuperación de puntos", curso_tipo: "recuperacion_puntos",
    codigo: "CUR-P4RT9Z", estado: "abierta", lugar: "Aula 2 · sede centro", plazas_max: 15, plazas_min: 10,
    ...profesor("inst-001"),
    sesiones: [0, 1, 2].map((n) => ({ sesion_id: `ed-puntos-1-s${n + 1}`, fecha: ymd(ptsIni.add(n, "day")), inicio: "16:00", fin: "20:00" })),
  };
  ediciones.push(ePts);
  for (let i = 0; i < 9; i += 1) {
    inscripciones.push(insc(ePts, persona(22 + i), {
      estado: "plaza",
      pago: i % 3 === 2 ? "pendiente" : "pagado",
      asistencia_confirmada: i < 4,
      ...(i === 5 ? { origen: "crm" } : {}),
    }));
  }

  // ── CAP inicial acelerado: 20 días de 7 h (140 h), 4/25 ──
  const ciIni = nextMonday(3);
  const eCi = {
    edicion_id: "ed-cap-inicial-1", curso_id: "cur-cap-inicial", curso_nombre: "CAP inicial acelerado", curso_tipo: "cap_inicial",
    codigo: "CUR-C1N8VA", estado: "abierta", lugar: "Aula 1 · sede centro", plazas_max: 25, plazas_min: 10,
    ...profesor("inst-003"), sesiones: sesionesLaborables("ed-cap-inicial-1", ciIni, 20, "08:00", "15:00"),
  };
  ediciones.push(eCi);
  [desdeAlumno("alu-005", { permiso: "C", origen: "crm" }), persona(31, { permiso: "C" }), persona(32, { permiso: "D" }), persona(33, { permiso: "C" })]
    .filter(Boolean)
    .forEach((p, i) => inscripciones.push(insc(eCi, p, { estado: "plaza", pago: i === 0 ? "pendiente" : "pagado" })));

  // ── CAP formación continua de hace dos meses: finalizada, con asistencia y certificados ──
  const oldIni = nextMonday(-10);
  const eOld = {
    edicion_id: "ed-cap-continuo-0", curso_id: "cur-cap-continuo", curso_nombre: "CAP formación continua 35 h", curso_tipo: "cap_continuo",
    codigo: "CUR-2HD5WE", estado: "finalizada", lugar: "Aula 1 · sede centro", plazas_max: 20, plazas_min: 8,
    ...profesor("inst-002"), sesiones: sesionesLaborables("ed-cap-continuo-0", oldIni, 5, "08:00", "15:00"),
  };
  ediciones.push(eOld);
  const ultimo = eOld.sesiones[eOld.sesiones.length - 1].fecha;
  const emitido = dayjs(ultimo).add(1, "day").hour(10).toISOString();
  for (let i = 0; i < 12; i += 1) {
    const asistencia = Object.fromEntries(eOld.sesiones.map((s, n) => [s.sesion_id, !(i === 9 && n === 2)]));
    const completo = i !== 9;
    inscripciones.push(insc(eOld, persona(i + 36, { permiso: "C" }), {
      estado: "plaza", pago: "pagado", asistencia, asistencia_confirmada: true,
      ...(completo ? { certificado_emitido_at: emitido, cap_renovado: { caducidad: ymd(dayjs(ultimo).add(5, "year")) } } : {}),
    }));
  }

  for (const e of ediciones) e.horas_total = horasTotales(e.sesiones);
  return { cursos, ediciones, inscripciones };
}

function ensure() {
  if (!state) state = seed();
  return state;
}

function recontar(ed) {
  const s = ensure();
  const de = s.inscripciones.filter((i) => i.edicion_id === ed.edicion_id);
  ed.ocupadas = de.filter((i) => i.estado === "plaza").length;
  ed.en_espera = de.filter((i) => i.estado === "espera").length;
  let n = 0;
  de.filter((i) => i.estado === "espera")
    .sort((a, b) => (a.puesto_espera || 0) - (b.puesto_espera || 0))
    .forEach((i) => { n += 1; i.puesto_espera = n; });
}

function estadoVisible(ed) {
  if (ed.estado === "finalizada") return "finalizada";
  if (ed.estado === "cerrada") return "cerrada";
  return Number(ed.ocupadas || 0) >= Number(ed.plazas_max || 0) ? "llena" : "abierta";
}

function vistaEdicion(ed) {
  recontar(ed);
  return { ...ed, estado_visible: estadoVisible(ed), enlace: undefined };
}

function precioEdicion(ed) {
  const curso = ensure().cursos.find((c) => c.curso_id === ed.curso_id);
  return ed.precio_cents != null ? ed.precio_cents : Number(curso?.precio_cents || 0);
}

// Al liberarse plazas: se ofrece a los primeros de la lista (marca `oferta_at`) y, en la demo, el
// primero pulsa «Lo quiero» a los pocos segundos y se queda la plaza (como gana el primero en la real).
function ofrecerPlazas(ed) {
  if (ed.estado !== "abierta") return;
  recontar(ed);
  const libres = Number(ed.plazas_max) - Number(ed.ocupadas);
  if (libres <= 0) return;
  const espera = ensure().inscripciones
    .filter((i) => i.edicion_id === ed.edicion_id && i.estado === "espera")
    .sort((a, b) => a.puesto_espera - b.puesto_espera)
    .slice(0, libres);
  if (!espera.length) return;
  const now = new Date().toISOString();
  espera.forEach((i) => { if (!i.oferta_at) i.oferta_at = now; });
  const primero = espera[0];
  const timer = setTimeout(() => {
    _timers.delete(timer);
    const s = state;
    if (!s) return;
    const e = s.ediciones.find((x) => x.edicion_id === ed.edicion_id);
    const i = s.inscripciones.find((x) => x.inscripcion_id === primero.inscripcion_id);
    if (!e || !i || i.estado !== "espera" || e.estado !== "abierta") return;
    recontar(e);
    if (e.ocupadas >= e.plazas_max) return;
    i.estado = "plaza";
    i.puesto_espera = undefined;
    i.oferta_at = undefined;
    i.pago = precioEdicion(e) > 0 ? "pendiente" : "no_aplica";
    recontar(e);
    emit();
    emitEvent({ kind: "oferta_aceptada", edicion_id: e.edicion_id, inscripcion: { ...i } });
  }, OFERTA_ACEPTADA_MS);
  _timers.add(timer);
}

// ── API (misma forma que cursosApi.js de la real) ────────────────────────────

export function getCursosSync() {
  const s = ensure();
  return { cursos: s.cursos.map((c) => ({ ...c })), ediciones: s.ediciones.map(vistaEdicion) };
}

export async function getCursos() {
  return getCursosSync();
}

export function getEdicionSync(edicionId) {
  const s = ensure();
  const ed = s.ediciones.find((e) => e.edicion_id === edicionId);
  if (!ed) return null;
  return {
    edicion: vistaEdicion(ed),
    inscripciones: s.inscripciones.filter((i) => i.edicion_id === edicionId).map((i) => ({ ...i, asistencia: { ...(i.asistencia || {}) } })),
    precio_cents: precioEdicion(ed),
    wa_number: CURSOS_WA_NUMBER,
  };
}

export async function getEdicion(edicionId) {
  const r = getEdicionSync(edicionId);
  if (!r) {
    const err = new Error("not_found");
    err.response = { status: 404, data: { error: "not_found" } };
    throw err;
  }
  return r;
}

export async function saveCurso(body) {
  await wait();
  const s = ensure();
  if (!body.tipo) throw fail("tipo_invalido");
  if (!String(body.nombre || "").trim()) throw fail("nombre_obligatorio");
  if (body.curso_id) {
    const c = s.cursos.find((x) => x.curso_id === body.curso_id);
    if (!c) throw fail("not_found");
    Object.assign(c, body);
    s.ediciones.filter((e) => e.curso_id === c.curso_id).forEach((e) => { e.curso_nombre = c.nombre; e.curso_tipo = c.tipo; });
    emit();
    return { curso: { ...c } };
  }
  const c = { activo: true, ...body, curso_id: nid("cur") };
  s.cursos.push(c);
  emit();
  return { curso: { ...c } };
}

const CODIGO_CHARS = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
function nuevoCodigo() {
  const usados = new Set(ensure().ediciones.map((e) => e.codigo));
  let c;
  do {
    c = "CUR-" + Array.from({ length: 6 }, () => CODIGO_CHARS[Math.floor(Math.random() * CODIGO_CHARS.length)]).join("");
  } while (usados.has(c));
  return c;
}

export async function saveEdicion(body) {
  await wait();
  const s = ensure();
  if (!body.sesiones || !body.sesiones.length) throw fail("sesiones_obligatorias");
  const max = Number(body.plazas_max);
  if (!Number.isInteger(max) || max < 1 || max > 500) throw fail("plazas_max_invalidas");
  if (Number(body.plazas_min || 0) > max) throw fail("plazas_min_invalidas");
  if (body.edicion_id) {
    const ed = s.ediciones.find((e) => e.edicion_id === body.edicion_id);
    if (!ed) throw fail("not_found");
    if (ed.estado === "finalizada") throw fail("edicion_finalizada");
    recontar(ed);
    if (max < ed.ocupadas) throw fail("plazas_por_debajo_de_ocupadas");
    const fechasCambian = JSON.stringify(ed.sesiones.map((x) => [x.fecha, x.inicio, x.fin])) !== JSON.stringify(body.sesiones.map((x) => [x.fecha, x.inicio, x.fin]));
    Object.assign(ed, {
      ...body,
      sesiones: body.sesiones.map((x, i) => ({ ...x, sesion_id: x.sesion_id || `${ed.edicion_id}-s${Date.now().toString(36)}${i}` })),
    });
    ed.horas_total = horasTotales(ed.sesiones);
    // Cambiar las fechas borra la confirmación de los inscritos (era de la fecha vieja), como en la real.
    if (fechasCambian) s.inscripciones.filter((i) => i.edicion_id === ed.edicion_id).forEach((i) => { i.asistencia_confirmada = false; });
    ofrecerPlazas(ed);
    emit();
    return { edicion: vistaEdicion(ed) };
  }
  const curso = s.cursos.find((c) => c.curso_id === body.curso_id);
  if (!curso) throw fail("not_found");
  const edicion_id = nid("ed");
  const ed = {
    estado: "abierta",
    ...body,
    edicion_id,
    curso_nombre: curso.nombre,
    curso_tipo: curso.tipo,
    codigo: nuevoCodigo(),
    sesiones: body.sesiones.map((x, i) => ({ ...x, sesion_id: `${edicion_id}-s${i + 1}` })),
  };
  ed.horas_total = horasTotales(ed.sesiones);
  s.ediciones.push(ed);
  emit();
  return { edicion: vistaEdicion(ed) };
}

export async function addInscripcion(edicionId, body) {
  await wait();
  const s = ensure();
  const ed = s.ediciones.find((e) => e.edicion_id === edicionId);
  if (!ed) throw fail("not_found");
  if (ed.estado === "finalizada") throw fail("edicion_finalizada");
  if (ed.estado === "cerrada") throw fail("edicion_cerrada");
  if (!/^\+[1-9]\d{7,14}$/.test(String(body.phone || ""))) throw fail("telefono_invalido");
  const ya = s.inscripciones.find((i) => i.edicion_id === edicionId && i.phone === body.phone && i.estado !== "cancelada");
  if (ya) throw fail("ya_inscrito");
  recontar(ed);
  const aEspera = body.lista_espera || ed.ocupadas >= ed.plazas_max;
  // Si el teléfono es el de un alumno, se vincula a su ficha (como hace el backend por teléfono).
  const alumno = (SECTOR_DATA_MAP.autoescuela?.mockAlumnos || []).find((a) => a.phone === body.phone);
  const nueva = insc(ed, {
    nombre: body.nombre, apellidos: body.apellidos || "", phone: body.phone, dni: body.dni || "", email: body.email || "",
    ...(body.permiso ? { permiso: body.permiso } : {}),
    ...(body.cap_caducidad ? { cap_caducidad_actual: body.cap_caducidad } : {}),
    ...(alumno ? { alumno_id: alumno.alumno_id, contact_id: `contact-${alumno.alumno_id}` } : {}),
  }, {
    origen: "crm",
    created_at: new Date().toISOString(),
    estado: aEspera ? "espera" : "plaza",
    pago: !aEspera && precioEdicion(ed) > 0 ? "pendiente" : "no_aplica",
    ...(aEspera ? { puesto_espera: ed.en_espera + 1 } : {}),
  });
  s.inscripciones.push(nueva);
  recontar(ed);
  emit();
  return { inscripcion: { ...nueva } };
}

export async function accionInscripcion(edicionId, inscId, body) {
  // Asistencia: instantánea (la real es optimista; aquí no hace falta latencia).
  if (body.accion !== "asistencia") await wait();
  const s = ensure();
  const ed = s.ediciones.find((e) => e.edicion_id === edicionId);
  const i = s.inscripciones.find((x) => x.inscripcion_id === inscId && x.edicion_id === edicionId);
  if (!ed || !i) throw fail("not_found");
  switch (body.accion) {
    case "pagado":
      i.pago = "pagado";
      i.pago_metodo = body.metodo || "efectivo";
      break;
    case "editar": {
      const { accion, cap_caducidad, ...datos } = body; // eslint-disable-line no-unused-vars
      Object.assign(i, datos);
      if (cap_caducidad !== undefined) i.cap_caducidad_actual = cap_caducidad || undefined;
      // Completar los datos a mano quita el «Faltan datos» (llegó sin Flow, solo con el nombre del perfil).
      i.datos_pendientes = !String(i.nombre || "").trim();
      if (i.dni) i.dni_revisar = false;
      break;
    }
    case "a_plaza":
      if (i.estado !== "espera") throw fail("no_en_espera");
      recontar(ed);
      if (ed.ocupadas >= ed.plazas_max) throw fail("llena");
      i.estado = "plaza";
      i.puesto_espera = undefined;
      i.oferta_at = undefined;
      i.pago = precioEdicion(ed) > 0 ? "pendiente" : "no_aplica";
      break;
    case "cancelar": {
      if (i.estado === "cancelada") throw fail("ya_cancelada");
      const teniaPlaza = i.estado === "plaza";
      i.estado = "cancelada";
      i.cancel_origen = "crm";
      i.puesto_espera = undefined;
      recontar(ed);
      if (teniaPlaza) ofrecerPlazas(ed);
      break;
    }
    case "asistencia":
      if (i.estado !== "plaza") throw fail("sin_plaza");
      i.asistencia = { ...(i.asistencia || {}), [body.sesion_id]: !!body.asistio };
      break;
    default:
      throw fail("accion_invalida");
  }
  recontar(ed);
  emit();
  return { inscripcion: { ...i } };
}

/**
 * Certificados: uno por asistente con TODAS las sesiones. En CAP, su ficha de alumno guarda el CAP
 * renovado (último día + 5 años, nunca acorta); sin ficha → `sin_alumno`. `url` es un id de vista
 * previa del simulador (no hay PDF de verdad): la página abre el certificado en un modal.
 */
export async function emitirCertificados(edicionId, body = {}) {
  await wait(DEMO_LATENCY_MS * 2);
  const s = ensure();
  const ed = s.ediciones.find((e) => e.edicion_id === edicionId);
  if (!ed) throw fail("not_found");
  let lista = s.inscripciones.filter((i) => i.edicion_id === edicionId && i.estado === "plaza" && asistenciaCompleta(i, ed));
  if (Array.isArray(body.inscripcion_ids)) lista = lista.filter((i) => body.inscripcion_ids.includes(i.inscripcion_id));
  const ultimo = ed.sesiones[ed.sesiones.length - 1]?.fecha;
  const now = new Date().toISOString();
  const cap = { sin_alumno: [], ya_posterior: [] };
  for (const i of lista) {
    i.certificado_emitido_at = now;
    if (esCap(ed.curso_tipo) && ultimo) {
      const caducidad = ymd(dayjs(ultimo).add(5, "year"));
      i.cap_renovado = { caducidad };
      const alumno = i.alumno_id ? alumnoPorId(i.alumno_id) : null;
      if (!alumno) cap.sin_alumno.push(i.inscripcion_id);
      else if (alumno.cap?.caducidad && alumno.cap.caducidad >= caducidad) cap.ya_posterior.push(i.inscripcion_id);
      else alumno.cap = { caducidad, obtencion: ultimo };
    }
  }
  if (body.finalizar) ed.estado = "finalizada";
  emit();
  return {
    certificados: lista.map((i) => ({ inscripcion_id: i.inscripcion_id, nombre: `${i.nombre || ""} ${i.apellidos || ""}`.trim(), url: `sim-cert:${i.inscripcion_id}` })),
    todos_url: lista.length > 1 ? `sim-cert-todos:${edicionId}` : null,
    cap,
    enviados_whatsapp: body.enviar_whatsapp ? lista.length : 0,
  };
}

// ── Navegación del simulador y suscripciones ─────────────────────────────────

export function setSelectedEdicionId(id) { _selectedEdicionId = id || null; }
export function getSelectedEdicionId() { return _selectedEdicionId; }

export function subscribeCursos(fn) {
  _subs.add(fn);
  return () => _subs.delete(fn);
}
export function subscribeCursoEventos(fn) {
  _eventSubs.add(fn);
  return () => _eventSubs.delete(fn);
}

/** Solo tests: vuelve a los datos de demo. */
export function resetCursos() {
  _timers.forEach((t) => clearTimeout(t));
  _timers.clear();
  state = null;
  _selectedEdicionId = null;
  emit();
}
